import tracking from "frontend/tracking";
import React, { useCallback, useEffect, useState } from "react";
import { equals, pluck, fromPairs } from "rambda";
import consts from "shared/consts";

import { CanvasElement } from "shared/datamodel/schemas/canvas-element";
import { Shape } from "shared/datamodel/schemas/shape";
import { StickyNote } from "shared/datamodel/schemas/sticky-note";
import { Drawing } from "shared/datamodel/schemas/drawing";
import { TextBlock } from "shared/datamodel/schemas/textBlock";
import { Connector } from "shared/datamodel/schemas/connector";
import { Frame } from "shared/datamodel/schemas/frame";
import { IntegrationItem } from "shared/datamodel/schemas/integration-item";
import { IntegrationType } from "shared/integrations/integration";

import { ColorPicker } from "frontend/canvas-designer-new/elements-toolbar/widgets/color-picker";
import { TextSizePicker } from "frontend/canvas-designer-new/elements-toolbar/widgets/textSizePicker";
import TextAlignmentPicker from "frontend/canvas-designer-new/elements-toolbar/widgets/textAlignmentPicker";
import TextProps from "./widgets/text-props";
import { ShapePicker } from "frontend/canvas-designer-new/elements-toolbar/widgets/shapePicker";
import { WidthPicker } from "frontend/canvas-designer-new/elements-toolbar/widgets/widthPicker";
import { ArrowheadPicker } from "./widgets/arrowHeadPicker";
import { IntegrationItemColorPicker } from "./widgets/integration-item-color-picker";

import {
  stickyNoteTraits,
  stickyNoteValidateTrait,
} from "frontend/canvas-designer-new/elements/sticky-note/sticky-note-element";
import { shapeTraits, shapeValidateTrait } from "frontend/canvas-designer-new/elements/shape-element";
import { drawingTraits } from "frontend/canvas-designer-new/elements/drawing-element";
import { textBlockTraits, textBlockValidateTrait } from "frontend/canvas-designer-new/elements/text-block-element";
import {
  connectorTraits,
  connectorValidateTrait,
} from "frontend/canvas-designer-new/elements/connector/connector-canvas-element";
import {
  integrationItemTraits,
  integrationItemValidateTrait,
} from "frontend/canvas-designer-new/elements/integration-element/integration-element";
import { ITraits, Trait, TraitToFieldName, TraitTrackingName } from "./elements-toolbar-types";
import { getElementTypeForId, isElementInGantt } from "../elements/canvas-elements-utils";
import { Text } from "frontend/icons/icons";
import toolbar_styles from "./elements-toolbar.module.css";
import { DropdownButton } from "./toolbar-buttons";
import cn from "classnames";
import { getPlatformType, PlatformType } from "frontend/utils/navigator-utils";
import { FontProperties } from "shared/datamodel/schemas/text-enabled-schema";
import TextFontPicker from "./widgets/text-font-picker";
import { frameTraits, frameValidateTrait } from "../elements/frame/frame-element";
import VisiblePicker from "./widgets/frameVisiblePicker";
import AspectRatioPicker from "./widgets/frameAspectRatioPicker";
import ConnectorLineStyle from "./widgets/connetor-line-style";
import ShapeStrokeStyle, { OrgChartNodeStroke } from "./widgets/shape-stroke-style-picker";
import { SubmenuDataUpdate } from "frontend/canvas-designer-new/canvas-submenu";
import { mindmapTraits, mindmapValidateTrait } from "../mindmap-element";
import { MindmapNodeElement } from "shared/datamodel/schemas/mindmap";
import MindmapOrientationPicker from "./widgets/mindmapOrientationPicker";
import { liveIntegrationTraits, liveIntegrationValidateTraits } from "../elements/card-stack/live-card-stack";
import { LiveIntegrationElement } from "shared/datamodel/schemas/live-integration";
import LiveIntegrationTraitMenuItem from "./widgets/live-integration/liveIntegrationTraitMenuItem";

import { MindmapOrgChartNodeElement } from "shared/datamodel/schemas/mindmap-org-chart";
import { mindmapOrgChartTraits, mindmapOrgChartValidateTraits } from "../elements/org-chart/mindmap-org-chart-element";
import { orgChartTraits, orgChartValidateTraits, parseNodeId } from "../elements/org-chart/orgchart-utils";
import MindmapOrgChartFieldsPicker from "./widgets/mindmapOrgChart/mindmapOrgChartFieldsPicker";
import MindmapOrgChartColorPicker from "./widgets/mindmapOrgChart/mindmapOrgChartColorPicker";
import MindmapOrgChartFilterPicker from "./widgets/mindmapOrgChart/mindmapOrgChartFilterPicker";
import { DRAW_PROPS_ID, OrgChartElement } from "shared/datamodel/schemas/org-chart";
import OrgchartOrientationPicker from "./widgets/orgchart/orgchart-orientation-picker";
import * as immer from "immer";
import { useAtomValue } from "jotai";
import { transformerRefAtom } from "state-atoms";
import OrgChartSingleItemView from "./widgets/orgchart/orgchart-item-view-type";
import ToolbarDatePicker from "./widgets/toolbarDatePicker";
import { timelineTraits, timelineValidateTrait } from "../elements/timeline/timeline-element";
import { TimelineElement } from "shared/datamodel/schemas/timeline";
import { TypeTableElement } from "shared/datamodel/schemas/table";
import {
  tableCellTraits,
  tableCellValidateTraits,
  tableTraits,
  tableValidateTraits,
} from "../elements/table/table-element";
import { AddColumnButton, AddRowButton } from "./widgets/table/table-structure-widgets";
import { getElementProvider } from "elements/index";
import { M } from "shared/datamodel/mutators";
import { Reflect } from "@workcanvas/reflect/client";
import UndoRedoStack from "utils/undo-redo/undo-redo-stack";
import { CheckoutSources } from "frontend/billing/checkout";
import { iconTraits, iconValidateTrait } from "elements/icon/icon-component";
import { IconElement } from "elements/icon/schema";
import { taskTraits, taskValidateTrait } from "elements/task/components/task-component";
import { TaskElement } from "elements/task/schema";

immer.enablePatches();

export function getTraitsForElement(elementType: string, element: CanvasElement, elementId: string): ITraits | null {
  if (!element) return null; // can happen on element's first render
  switch (elementType) {
    case consts.CANVAS_ELEMENTS.STICKY_NOTE: {
      return stickyNoteTraits(element as StickyNote);
    }
    case consts.CANVAS_ELEMENTS.SHAPE: {
      return shapeTraits(element as Shape);
    }
    case consts.CANVAS_ELEMENTS.TEXT_BLOCK: {
      return textBlockTraits(element as TextBlock);
    }
    case consts.CANVAS_ELEMENTS.DRAWING: {
      return drawingTraits(element as Drawing);
    }
    case consts.CANVAS_ELEMENTS.CONNECTOR: {
      return connectorTraits(element as Connector);
    }
    case consts.CANVAS_ELEMENTS.FRAME: {
      return frameTraits(element as Frame);
    }
    case consts.CANVAS_ELEMENTS.MINDMAP: {
      return mindmapTraits(element as MindmapNodeElement);
    }
    case consts.CANVAS_ELEMENTS.MINDMAP_ORG_CHART: {
      return mindmapOrgChartTraits(element as MindmapOrgChartNodeElement);
    }
    case consts.CANVAS_ELEMENTS.ORG_CHART: {
      return orgChartTraits(element as OrgChartElement, elementId);
    }
    case consts.CANVAS_ELEMENTS.ORG_CHART_NODE: {
      return orgChartTraits(element as OrgChartElement, elementId);
    }
    case consts.CANVAS_ELEMENTS.LIVE_INTEGRATION: {
      return liveIntegrationTraits(element as LiveIntegrationElement);
    }
    case consts.CANVAS_ELEMENTS.TIMELINE: {
      return timelineTraits(element as TimelineElement);
    }
    case consts.CANVAS_ELEMENTS.TABLE: {
      return tableTraits(element as TypeTableElement);
    }
    case consts.CANVAS_ELEMENTS.TABLE_CELL: {
      return tableCellTraits(element as any);
    }
    case consts.CANVAS_ELEMENTS.INTEGRATION: {
      return integrationItemTraits(element as IntegrationItem);
    }
    case consts.CANVAS_ELEMENTS.ICON: {
      return iconTraits(element as IconElement);
    }
    case consts.CANVAS_ELEMENTS.TASK: {
      return taskTraits(element as TaskElement);
    }
    case consts.CANVAS_ELEMENTS.TASK_CARD:
    case consts.CANVAS_ELEMENTS.FILE:
    case consts.CANVAS_ELEMENTS.PDF:
    case consts.CANVAS_ELEMENTS.CARD_STACK: {
      return null;
    }
    default: {
      return null;
    }
  }
}

function validateTraitForElement(elementType: string, element: CanvasElement, trait: Trait, newValue: any): any {
  switch (elementType) {
    case consts.CANVAS_ELEMENTS.STICKY_NOTE: {
      return stickyNoteValidateTrait(element as StickyNote, trait, newValue);
    }
    case consts.CANVAS_ELEMENTS.CONNECTOR: {
      return connectorValidateTrait(element as Connector, trait, newValue);
    }
    case consts.CANVAS_ELEMENTS.SHAPE: {
      return shapeValidateTrait(element as Shape, trait, newValue);
    }
    case consts.CANVAS_ELEMENTS.TEXT_BLOCK: {
      return textBlockValidateTrait(element as TextBlock, trait, newValue);
    }
    case consts.CANVAS_ELEMENTS.FRAME: {
      return frameValidateTrait(element as Frame, trait, newValue);
    }
    case consts.CANVAS_ELEMENTS.MINDMAP: {
      return mindmapValidateTrait(element as MindmapNodeElement, trait, newValue);
    }
    case consts.CANVAS_ELEMENTS.ORG_CHART: {
      return orgChartValidateTraits(element as OrgChartElement, trait, newValue);
    }
    case consts.CANVAS_ELEMENTS.ORG_CHART_NODE: {
      return orgChartValidateTraits(element as OrgChartElement, trait, newValue);
    }
    case consts.CANVAS_ELEMENTS.MINDMAP_ORG_CHART: {
      return mindmapOrgChartValidateTraits(element as MindmapOrgChartNodeElement, trait, newValue);
    }
    case consts.CANVAS_ELEMENTS.TIMELINE: {
      return timelineValidateTrait(element as TimelineElement, trait, newValue);
    }
    case consts.CANVAS_ELEMENTS.TABLE: {
      return tableValidateTraits(element as TypeTableElement, trait, newValue);
    }
    case consts.CANVAS_ELEMENTS.TABLE_CELL: {
      return tableCellValidateTraits(element as any, trait, newValue);
    }
    case consts.CANVAS_ELEMENTS.INTEGRATION: {
      return integrationItemValidateTrait(element as IntegrationItem, trait, newValue);
    }
    case consts.CANVAS_ELEMENTS.ICON: {
      return iconValidateTrait(element as IconElement, trait, newValue);
    }
    case consts.CANVAS_ELEMENTS.TASK: {
      return taskValidateTrait(element as TaskElement, trait, newValue);
    }
    case consts.CANVAS_ELEMENTS.LIVE_INTEGRATION: {
      return liveIntegrationValidateTraits(element as LiveIntegrationElement, trait, newValue);
    }
    case consts.CANVAS_ELEMENTS.DRAWING:
    case consts.CANVAS_ELEMENTS.TASK_CARD:
    case consts.CANVAS_ELEMENTS.FILE:
    case consts.CANVAS_ELEMENTS.PDF:
    case consts.CANVAS_ELEMENTS.CARD_STACK: {
      return newValue;
    }
    default: {
      return newValue;
    }
  }
}

// some utility types and a higher order component to make it things easier for us
// this will help us convert single-item input fields to multi-item input fields

// this is the type of a component which represents a controlled-input for a single value
interface IControlledInput<T> {
  value: T;
  onChange: (newValue: T, addUndo?: boolean) => void;
  multi: boolean;
}

// this is what our multi-item fields accepts
interface IMultiTraits<ValueType> {
  values: ITraits[];
  onChange: (trait: Trait, newValue: ValueType, subMenuData?: any, addUndo?: boolean) => void;
  makeSubmenuData?: (newValue: ValueType) => any;
}

function multiValuePicker<ValueType>(Component: React.FC<IControlledInput<ValueType>>, trait: Trait) {
  // eslint-disable-next-line react/display-name
  return (props: any) => {
    const { values, onChange, makeSubmenuData, ...restProps }: IMultiTraits<ValueType> = props;
    if (values.length === 0 || !values.every((v) => trait in v)) return null;
    // find first non-null value
    const firstNonNull = values.find((v) => v[trait] != undefined && v[trait] != null);
    const valueToDisplay = firstNonNull ? firstNonNull[trait] : null;
    const isMulti = firstNonNull
      ? values.filter((v) => v[trait]).some((v) => !equals(v[trait], valueToDisplay))
      : false;

    return (
      <div>
        <Component
          value={valueToDisplay}
          onChange={(c: ValueType, addUndo: boolean = true) => {
            const subMenuData = makeSubmenuData ? makeSubmenuData(c) : null;
            if (subMenuData) onChange(trait, c, subMenuData, addUndo);
            else onChange(trait, c, undefined, addUndo);
          }}
          multi={isMulti}
          {...restProps}
        />
      </div>
    );
  };
}

// -----------------------------

const StickyNoteColors = consts.STICKY_NOTE_COLORS;

const ShapeColors = consts.COLOR_PALETTE_INCLUDING_WHITE_TRANSPARENT;

const StickyNoteColorPicker = multiValuePicker(ColorPicker, Trait.stickyNoteColor);

const TextColorPicker = multiValuePicker(ColorPicker, Trait.textColor);
const TextSizeMultiPicker = multiValuePicker(TextSizePicker, Trait.fontSize);
const TextAlignMultiPicker = multiValuePicker(TextAlignmentPicker, Trait.align);
const TextPropsMultiPicker = multiValuePicker(TextProps, Trait.fontProps);
const TextFontMultiPicker = multiValuePicker(TextFontPicker, Trait.font);

const ShapeMultiPicker = multiValuePicker(ShapePicker, Trait.shape);
const StrokeColorMultiPicker = multiValuePicker(ColorPicker, Trait.shapeStrokeColor);
const FillColorMultiPicker = multiValuePicker(ColorPicker, Trait.fillColor);
const DrawingLineWidthMultiPicker = multiValuePicker(WidthPicker, Trait.drawingLineWidth);
const LineColorMultiPicker = multiValuePicker(ColorPicker, Trait.lineColor);

const ArrowHeadMultiPicker = multiValuePicker(ArrowheadPicker, Trait.arrowHeads);

const FrameVisiblePicker = multiValuePicker(VisiblePicker, Trait.visible);
const FrameAspectRatioPicker = multiValuePicker(AspectRatioPicker, Trait.frameAspectRatio);

const IntegrationItemColorMultiPicker = multiValuePicker(IntegrationItemColorPicker, Trait.integrationItemColor);

const MindmapOrientation = multiValuePicker(MindmapOrientationPicker, Trait.mindmapOrientation);

const MindmapColorMultiPicker = multiValuePicker(ColorPicker, Trait.mindmapColor);

const MindmapOrgChartFields = multiValuePicker(MindmapOrgChartFieldsPicker, Trait.mindmapOrgChartFields);
const MindmapOrgChartColor = multiValuePicker(MindmapOrgChartColorPicker, Trait.mindmapOrgChartColor);
const MindmapOrgFilter = multiValuePicker(MindmapOrgChartFilterPicker, Trait.mindmapOrgChartColumnValues);
const LiveIntegrationMultiPicker = multiValuePicker(LiveIntegrationTraitMenuItem, Trait.liveIntegration);

const OrgChartOrientation = multiValuePicker(OrgchartOrientationPicker, Trait.orgchartOrientation);
const OrgChartFields = multiValuePicker(MindmapOrgChartFieldsPicker, Trait.orgChartFields);
const OrgChartNodeColor = multiValuePicker(ColorPicker, Trait.orgChartNodeColor);
const OrgChartItemView = multiValuePicker(OrgChartSingleItemView, Trait.orgChartItemViewType);

const StartDatePicker = multiValuePicker(ToolbarDatePicker, Trait.startDate);
const EndDatePicker = multiValuePicker(ToolbarDatePicker, Trait.endDate);

const TableFillColor = multiValuePicker(ColorPicker, Trait.tableFillColor);
const TableStrokeColor = multiValuePicker(ColorPicker, Trait.tableStrokeColor);
const TableAddColumn = multiValuePicker(AddColumnButton, Trait.tableAddColumn);
const TableAddRow = multiValuePicker(AddRowButton, Trait.tableAddRow);
// -----------------------------

export default function ElementsTraitsButtons({
  ids,
  elements,
  mutateElementsProperties,
  customColors = [],
  documentId,
  reflect,
  undoRedoStack,
  showUpgradeModal,
  selectedIds,
}: {
  ids: string[];
  elements: CanvasElement[];
  mutateElementsProperties: (
    info: Map<string, any>,
    undoInfo: Map<string, any>,
    subMenuData?: SubmenuDataUpdate,
    addUndo?: boolean
  ) => void;
  customColors?: string[];
  documentId?: string;
  reflect?: Reflect<M> | null;
  undoRedoStack: UndoRedoStack;
  showUpgradeModal?: ((reason: CheckoutSources) => void) | undefined;
  selectedIds?: string[];
}) {
  const [undoProps, setUndoProps] = useState<Map<string, any> | null>(null);
  const transformerRef = useAtomValue(transformerRefAtom);

  const elementTypes = ids.map((id) => getElementTypeForId(id));
  const traits: ITraits[] = [];
  for (let i = 0; i < ids.length; i++) {
    const tr = getTraitsForElement(elementTypes[i], elements[i]!, ids[i]);
    traits.push(tr || {});
  }

  const onTraitChange = useCallback(
    (trait: Trait, value: any, subMenuData?: SubmenuDataUpdate, addUndo: boolean = true) => {
      const props = new Map<string, any>();
      let prevProps = new Map<string, any>();

      for (let i = 0; i < ids.length; i++) {
        let newProps: any;
        let id = ids[i];

        if (trait === Trait.orgChartNodeColor) {
          const { nodeId, fullElementId } = parseNodeId(ids[i])!;
          // this will be better handled with immer patches
          // making a change with this approach (mutateElementsProperties) requires
          // I pass the entire 'data' field, which could overwrite changes by other users on other nodes.
          // eslint-disable-next-line react/prop-types
          const data = props.has(fullElementId) ? props.get(fullElementId).data : (elements[i] as OrgChartElement).data;
          newProps = {
            data: {
              ...data,
              [nodeId]: {
                ...data[nodeId],
                [DRAW_PROPS_ID]: {
                  ...data[nodeId][DRAW_PROPS_ID],
                  color: validateTraitForElement(elementTypes[i], elements[i], trait, value),
                },
              },
            },
          };
        } else if (trait === Trait.orgChartNodeStrokeDash) {
          const { nodeId, fullElementId } = parseNodeId(ids[i])!;
          // this will be better handled with immer patches
          // eslint-disable-next-line react/prop-types
          const data = props.has(fullElementId) ? props.get(fullElementId).data : (elements[i] as OrgChartElement).data;
          newProps = {
            data: {
              ...data,
              [nodeId]: {
                ...data[nodeId],
                [DRAW_PROPS_ID]: {
                  ...data[nodeId][DRAW_PROPS_ID],
                  dash: value,
                },
              },
            },
          };
        } else if (trait === Trait.orgChartNodeStrokeWidth) {
          const { nodeId, fullElementId } = parseNodeId(ids[i])!;
          // this will be better handled with immer patches
          // eslint-disable-next-line react/prop-types
          const data = props.has(fullElementId) ? props.get(fullElementId).data : (elements[i] as OrgChartElement).data;
          newProps = {
            data: {
              ...data,
              [nodeId]: {
                ...data[nodeId],
                [DRAW_PROPS_ID]: {
                  ...data[nodeId][DRAW_PROPS_ID],
                  lineWidth: value,
                },
              },
            },
          };
        } else {
          const validated = validateTraitForElement(elementTypes[i], elements[i], trait, value);
          if (typeof validated === "object" && !Array.isArray(validated)) {
            newProps = validated;
          } else {
            newProps = { [TraitToFieldName[trait]]: validated };
          }
        }

        if (
          trait == Trait.orgchartOrientation ||
          trait == Trait.orgChartFields ||
          trait == Trait.orgChartNodeColor ||
          trait == Trait.orgChartNodeStroke ||
          trait == Trait.orgChartNodeStrokeDash ||
          trait == Trait.orgChartNodeStrokeWidth ||
          trait == Trait.orgChartItemViewType
        ) {
          const { fullElementId } = parseNodeId(ids[i])!;
          id = fullElementId; // change the root orgchart element, not the child (which doesn't exist in reflect)
        }

        // eslint-disable-next-line react/prop-types
        props.set(id, newProps);

        const previousProps = elements[i]
          ? Object.fromEntries(Object.entries(elements[i] as any).filter(([key, _value]) => key in newProps))
          : {};
        prevProps.set(id, previousProps);
      }

      if (!addUndo && !undoProps) {
        setUndoProps(prevProps);
      }
      // should add undo action, but we already saved the initial state
      if (addUndo && undoProps) {
        prevProps = undoProps;
        setUndoProps(null); // reset
      }
      mutateElementsProperties(props, prevProps, subMenuData, addUndo);

      // track event only if we are adding undo action (which means we are not in the middle of a multi-action)
      if (addUndo) {
        tracking.trackEvent(
          consts.TRACKING_CATEGORY.CANVAS_ACTION,
          "element-context-menu",
          elementTypes.length == 1 ? elementTypes[0] : "multiple-elements",
          TraitTrackingName[trait]
        );
      }

      if (trait == Trait.frameAspectRatio) {
        setTimeout(() => transformerRef && transformerRef.current && transformerRef.current?.forceUpdate(), 5);
      }
    },
    [ids, elements, mutateElementsProperties, elementTypes, undoProps]
  );

  const sameType = elementTypes.every((elt) => elt == elementTypes[0]);
  const typeOfAll = sameType ? elementTypes[0] : null;

  const hasStickyNote = !!elementTypes.find((t) => t === consts.CANVAS_ELEMENTS.STICKY_NOTE);
  const isElementInsideGantt = elements.some((element) => {
    const element_ = element as CanvasElement & { type?: string; containerId?: string };
    return isElementInGantt(element_, true);
  });

  function makeBasicSubmenuData(trait: Trait, value: any) {
    return fromPairs(elementTypes.map((type: string) => [type, { [TraitToFieldName[trait]]: value }]));
  }

  function renderNewInfraToolbar() {
    if (!typeOfAll) {
      return null;
    }
    const provider = getElementProvider(typeOfAll);
    if (!provider?.getToolbarRenderer) {
      return null;
    }
    const ToolbarComponent = provider.getToolbarRenderer();
    return (
      <ToolbarComponent
        ids={ids}
        elements={elements}
        context={{ reflect, undoRedoStack, showUpgradeModal, documentId: documentId || "" }}
      />
    );
  }

  return (
    <>
      {renderNewInfraToolbar()}
      <FrameAspectRatioPicker values={traits} onChange={onTraitChange} />
      <FillColorMultiPicker
        values={traits}
        onChange={onTraitChange}
        tooltip="Fill color"
        colorPalette={ShapeColors}
        customColors={customColors}
        withTransparency
        allowCustomColor
        makeSubmenuData={(color: any) => {
          if (typeof color == "string") {
            return fromPairs(elementTypes.map((type: string) => [type, { color }]));
          }
          return null;
        }}
      />
      <StrokeColorMultiPicker
        values={traits}
        onChange={onTraitChange}
        tooltip="Stroke color"
        colorPalette={ShapeColors}
        customColors={customColors}
        onlyOutline
        withTransparency
        allowCustomColor
        makeSubmenuData={(border: any) => {
          if (typeof border == "string") return { shape: { border } };
          return null;
        }}
      />
      {typeOfAll == consts.CANVAS_ELEMENTS.INTEGRATION && !isElementInsideGantt && (
        <IntegrationItemColorMultiPicker
          values={traits}
          onChange={onTraitChange}
          tooltip="Color"
          documentId={documentId}
          itemId={elements.length === 1 ? (elements[0] as IntegrationItem)?.configuration?.itemId : undefined}
          colorPalette={ShapeColors}
          customColors={customColors}
          allowCustomColor
          allowSyncedColor={(elements as IntegrationItem[]).every(
            (e: IntegrationItem) =>
              e?.integrationType == IntegrationType.monday &&
              e?.integrationId === (elements as IntegrationItem[])[0].integrationId
          )}
        />
      )}
      <TableAddRow values={traits} onChange={onTraitChange} />
      <TableAddColumn values={traits} onChange={onTraitChange} />
      <TableFillColor
        values={traits}
        onChange={onTraitChange}
        tooltip="Fill color"
        colorPalette={ShapeColors}
        customColors={customColors}
        withTransparency
        allowCustomColor
        // makeSubmenuData={(color: any) => { }}
      />
      <TableStrokeColor
        values={traits}
        onChange={onTraitChange}
        tooltip="Table lines color"
        colorPalette={ShapeColors}
        customColors={customColors}
        onlyOutline
        allowCustomColor
        // makeSubmenuData={(border: string) => ({ shape: { border } })}
      />
      <MindmapColorMultiPicker
        values={traits}
        onChange={onTraitChange}
        tooltip="Stroke color"
        colorPalette={ShapeColors}
        customColors={customColors}
        allowCustomColor
      />
      <MindmapOrgFilter values={traits} onChange={onTraitChange} tooltip="Filter" documentId={documentId} />
      <MindmapOrgChartFields
        values={traits}
        onChange={onTraitChange}
        tooltip="Info to present"
        selectedIds={selectedIds}
      />
      <MindmapOrgChartColor values={traits} onChange={onTraitChange} tooltip="Color code" />
      {typeOfAll == consts.CANVAS_ELEMENTS.SHAPE && (
        <ShapeStrokeStyle
          value={traits}
          onChange={(trait, value) => {
            const submenuData = makeBasicSubmenuData(trait, value);
            onTraitChange(trait, value, submenuData);
          }}
        />
      )}
      <ShapeMultiPicker
        key="shape"
        values={traits}
        onChange={onTraitChange}
        tooltip="Shape"
        makeSubmenuData={(type: string) => {
          if (type in consts.SHAPES) {
            return { shape: { type, subtype: "" } };
          } else {
            return { shape: { type: consts.CANVAS_ELEMENTS.SHAPE, subtype: type } };
          }
        }}
      />
      <LineColorMultiPicker
        values={traits}
        colorPalette={ShapeColors}
        customColors={customColors}
        onChange={onTraitChange}
        tooltip="Line color"
        makeSubmenuData={(color: string) => {
          const data: any = {};
          if (elementTypes.indexOf(consts.CANVAS_ELEMENTS.CONNECTOR) != -1) {
            data.connector = { stroke: color };
          }
          if (elementTypes.indexOf(consts.CANVAS_ELEMENTS.DRAWING) != -1) {
            data.pen = { color };
          }
          return data;
        }}
        allowCustomColor
        withTransparency
      />
      <DrawingLineWidthMultiPicker values={traits} onChange={onTraitChange} tooltip="Line width" />
      {typeOfAll == consts.CANVAS_ELEMENTS.CONNECTOR && (
        <>
          <ConnectorLineStyle
            values={traits}
            onChange={(trait, value) => {
              const submenuData = makeBasicSubmenuData(trait, value);
              onTraitChange(trait, value, submenuData);
            }}
          />
          <ArrowHeadMultiPicker
            values={traits}
            onChange={(trait: Trait, value: [string, string]) => {
              let submenuData = undefined;
              if (value[1] !== "keep") {
                submenuData = { connector: { startArrow: value[1] } };
              }
              if (value[0] !== "keep") {
                submenuData = { connector: { ...submenuData?.connector, endArrow: value[0] } };
              }
              onTraitChange(trait, value, submenuData);
            }}
          />
        </>
      )}
      <StickyNoteColorPicker
        values={traits}
        onChange={onTraitChange}
        tooltip="Fill color"
        colorPalette={StickyNoteColors}
        makeSubmenuData={(color: string) => ({ stickyNote: { color } })}
      />
      <TextProperties
        values={traits}
        onChange={(trait, value) => {
          const submenuData = makeBasicSubmenuData(trait, value);
          onTraitChange(trait, value, submenuData);
        }}
        customColors={customColors}
        settings={{ maxFontSize: hasStickyNote ? consts.DEFAULTS.STICKY_NOTE_MAX_FONT_SIZE : undefined }}
      />
      <FrameVisiblePicker values={traits} onChange={onTraitChange} />
      <MindmapOrientation values={traits} onChange={onTraitChange} />
      <OrgChartFields values={traits} onChange={onTraitChange} />

      <OrgChartNodeColor
        values={traits}
        onChange={onTraitChange}
        tooltip="Color"
        colorPalette={consts.ORG_CHART_COLOR_PALETTE}
      />
      <OrgChartItemView values={traits} onChange={onTraitChange} />
      <OrgChartOrientation values={traits} onChange={onTraitChange} />

      {traits.every((trait) => trait.orgChartNodeStroke) && (
        <OrgChartNodeStroke values={pluck("orgChartNodeStroke", traits)} onChange={onTraitChange} />
      )}
      <LiveIntegrationMultiPicker values={traits} onChange={onTraitChange} documentId={documentId} />

      <StartDatePicker values={traits} onChange={onTraitChange} title={"From"} />
      <EndDatePicker values={traits} onChange={onTraitChange} title={"To"} />
    </>
  );
}

type TextPropertiesSettings = {
  maxFontSize?: number;
};

function TextProperties(props: {
  values: ITraits[];
  onChange: (trait: Trait, value: any) => void;
  customColors?: string[];
  settings?: TextPropertiesSettings;
}) {
  const { values, onChange, customColors = [], settings } = props;

  const traits: any = {
    [Trait.textColor]: 0,
    [Trait.font]: 0,
    [Trait.fontSize]: 0,
    [Trait.align]: 0,
    [Trait.fontProps]: 0,
  };
  let otherTraits = false;
  for (const v of values) {
    for (const key of Object.keys(v)) {
      if (key in traits) {
        traits[key]++;
      } else {
        otherTraits = true;
      }
    }
  }
  const allHaveProp = (x: any) => traits[x] == values.length;

  let widgets: JSX.Element[] | null = null;

  if (allHaveProp(Trait.textColor)) {
    widgets ||= [];
    widgets.push(
      <TextColorPicker
        key="text-color"
        values={values}
        colorPalette={ShapeColors}
        customColors={customColors}
        allowCustomColor
        onChange={onChange}
        tooltip="Text Color"
        withTransparency
      />
    );
  }

  if (allHaveProp(Trait.font)) {
    widgets ||= [];
    widgets.push(<TextFontMultiPicker key="text-font" values={values} onChange={onChange} tooltip="Font" />);
  }

  if (allHaveProp(Trait.fontSize)) {
    widgets ||= [];
    widgets.push(
      <TextSizeMultiPicker
        key="text-font-size"
        values={values}
        onChange={onChange}
        maxFontSize={settings?.maxFontSize}
      />
    );
  }

  if (allHaveProp(Trait.align)) {
    widgets ||= [];
    widgets.push(<TextAlignMultiPicker key="text-align" values={values} onChange={onChange} />);
  }
  if (allHaveProp(Trait.fontProps)) {
    widgets ||= [];
    widgets.push(<TextPropsMultiPicker key="font-props" values={values} onChange={onChange} />);
  }

  useEffect(() => {
    if (!values || values.length === 0) return;

    function keyHandle(e: KeyboardEvent) {
      const platform = getPlatformType();
      const fnKey = platform == PlatformType.Macintosh ? e.metaKey : e.ctrlKey;
      if (fnKey) {
        let newProps;
        switch (e.code) {
          case "KeyB": {
            newProps = values[0].fontProps ^ FontProperties.Bold;
            break;
          }
          case "KeyU": {
            newProps = values[0].fontProps ^ FontProperties.Underline;
            break;
          }
          case "KeyI": {
            newProps = values[0].fontProps ^ FontProperties.Italic;
            break;
          }
        }
        if (newProps != undefined) {
          onChange(Trait.fontProps, newProps);
          // if the focused element is contenteditable, the browser treats it as rich-text and
          // will apply its own formatting, so we need to prevent the default action
          e.preventDefault();
        }
      }
    }

    if (allHaveProp(Trait.fontProps)) {
      window.document.body.addEventListener("keydown", keyHandle);
      return () => window.document.body.removeEventListener("keydown", keyHandle);
    }
  }, [values]);

  if (widgets == null) {
    return null;
  }
  if (otherTraits == false && widgets) {
    return <>{widgets}</>;
  }

  return (
    <div>
      <DropdownButton
        data-testid="TextProps"
        tooltip="Text properties"
        renderPopup={() => (
          <div className={cn(toolbar_styles.miniToolbarLayout, toolbar_styles.withSeparators)}>{widgets}</div>
        )}
        arrowSize={12}
        className={toolbar_styles.miniToolbarPopup}
      >
        <Text size={18} />
      </DropdownButton>
    </div>
  );
}
